import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import api from '../config/api';
import { DOCUMENT } from '../constants/documentTheme';
import { partnerOutlineBtnSx, partnerOxbloodBtnSx } from './PartnerListChrome';
import PartnerTripRequestDrawer from './PartnerTripRequestDrawer';
import CreateOpsRequestDialog from './CreateOpsRequestDialog';

function parseTripDate(value) {
  if (!value) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const parsed = new Date(raw.includes('T') ? raw : `${raw}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function startOfDay(date = new Date()) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function tripDayNumber(trip, now = new Date()) {
  const start = parseTripDate(trip?.start_date);
  if (!start) return null;
  const today = startOfDay(now);
  const startDay = startOfDay(start);
  return Math.floor((today.getTime() - startDay.getTime()) / 86400000) + 1;
}

function tripTotalDays(trip) {
  const start = parseTripDate(trip?.start_date);
  const end = parseTripDate(trip?.end_date);
  if (!start || !end) return null;
  return Math.floor((startOfDay(end).getTime() - startOfDay(start).getTime()) / 86400000) + 1;
}

const PartnerTripOperationsPanel = ({
  tripId,
  trip,
  onOpenItinerary,
  onDownloadPdf,
  onEmailItinerary,
  pdfBusy = false,
  onChanged,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const tripDayLabel = useMemo(() => {
    const day = tripDayNumber(trip);
    const total = tripTotalDays(trip);
    if (!day || !total || day < 1) return null;
    return t('partnerOps.tripDayLabel', { day, total });
  }, [trip, t]);

  const loadData = useCallback(async () => {
    if (!tripId) return;
    try {
      setLoading(true);
      setError('');
      const [requestsRes, timelineRes] = await Promise.all([
        api.get(`/partners/requests?tripId=${tripId}`, { usePartnerAuth: true }),
        api.get(`/partners/trips/${tripId}/operations-timeline`, { usePartnerAuth: true }).catch(() => ({ data: { events: [] } })),
      ]);
      setRequests(requestsRes.data?.requests || []);
      setTimeline(timelineRes.data?.events || []);
    } catch (err) {
      setError(err.response?.data?.error || t('partnerOps.loadFailed'));
      setRequests([]);
      setTimeline([]);
    } finally {
      setLoading(false);
    }
  }, [tripId, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openRequests = useMemo(
    () => requests.filter((request) => request.status !== 'resolved' && request.status !== 'cancelled'),
    [requests]
  );

  const formatEventTime = (value) => {
    if (!value) return '';
    try {
      return format(parseISO(String(value).replace(' ', 'T')), 'MMM d, h:mm a');
    } catch {
      return String(value);
    }
  };

  const messagePath = trip?.inquiry_id
    ? `/partner/inquiries/${trip.inquiry_id}`
    : null;

  return (
    <Box data-testid="partner-trip-operations-panel">
      {tripDayLabel ? (
        <Typography sx={{ fontSize: 14, color: DOCUMENT.ink2, mb: 2 }}>
          {tripDayLabel}
        </Typography>
      ) : null}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2.5 }}>
        <Button
          size="small"
          sx={partnerOxbloodBtnSx}
          onClick={() => setCreateOpen(true)}
          data-testid="partner-trip-new-request"
        >
          {t('partnerOps.newRequest')}
        </Button>
        <Button
          size="small"
          sx={partnerOutlineBtnSx}
          onClick={() => onOpenItinerary?.()}
        >
          {t('partnerOps.quickActions.itinerary')}
        </Button>
        <Button
          size="small"
          sx={partnerOutlineBtnSx}
          onClick={() => onDownloadPdf?.()}
          disabled={!onDownloadPdf || pdfBusy}
          data-testid="partner-ops-download-pdf"
        >
          {t('partnerManage.downloadPdf')}
        </Button>
        <Button
          size="small"
          sx={partnerOutlineBtnSx}
          onClick={() => onEmailItinerary?.()}
          disabled={!onEmailItinerary}
          data-testid="partner-ops-email-itinerary"
        >
          {t('partnerManage.emailItinerary')}
        </Button>
        {messagePath ? (
          <Button
            size="small"
            sx={partnerOutlineBtnSx}
            onClick={() => navigate(messagePath)}
          >
            {t('partnerOps.quickActions.message')}
          </Button>
        ) : null}
        <Button
          size="small"
          sx={partnerOutlineBtnSx}
          onClick={() => onOpenItinerary?.('add')}
        >
          {t('partnerOps.quickActions.addActivity')}
        </Button>
        <Button
          size="small"
          sx={partnerOutlineBtnSx}
          disabled
          title={t('partnerOps.quickActions.documentsHint')}
        >
          {t('partnerOps.quickActions.documents')}
        </Button>
      </Box>

      {error ? (
        <Typography sx={{ fontSize: 13, color: '#8C2B22', mb: 2 }}>{error}</Typography>
      ) : null}

      <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 20, mb: 1.5 }}>
        {t('partnerOps.openRequests')}
      </Typography>

      {loading ? (
        <Typography sx={{ fontSize: 13, color: DOCUMENT.ink3 }}>{t('partnerLists.loadingShort')}</Typography>
      ) : !openRequests.length ? (
        <Typography sx={{ fontSize: 14, color: DOCUMENT.ink2, mb: 2 }}>
          {t('partnerOps.noOpenRequests')}
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
          {openRequests.map((request) => (
            <Box
              key={request.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                flexWrap: 'wrap',
                p: 1.5,
                border: `1px solid ${DOCUMENT.ruleHard}`,
                bgcolor: DOCUMENT.surface,
              }}
              data-testid={`partner-trip-open-request-${request.id}`}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{request.title}</Typography>
                <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3, mt: 0.25 }}>
                  {t(`partnerOps.requestType.${request.type}`, { defaultValue: request.type })}
                  {' · '}
                  {request.age}
                  {' · '}
                  {t(`partnerOps.status.${request.status}`, { defaultValue: request.status })}
                </Typography>
              </Box>
              <Button
                size="small"
                sx={partnerOutlineBtnSx}
                onClick={() => {
                  setSelectedRequestId(request.id);
                  setDrawerOpen(true);
                }}
              >
                {t('partnerOps.view')}
              </Button>
            </Box>
          ))}
        </Box>
      )}

      {timeline.length ? (
        <>
          <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 20, mb: 1.5 }}>
            {t('partnerOps.timelineTitle')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {timeline.slice(0, 20).map((event) => (
              <Box
                key={event.id}
                sx={{
                  py: 1,
                  borderBottom: `1px solid ${DOCUMENT.rule}`,
                }}
              >
                <Typography sx={{ fontSize: 13, color: DOCUMENT.ink }}>
                  {event.requestTitle}
                  {' — '}
                  {t(`partnerOps.eventType.${event.eventType}`, { defaultValue: event.eventType })}
                </Typography>
                <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3, mt: 0.25 }}>
                  {[event.actorName, formatEventTime(event.createdAt)].filter(Boolean).join(' · ')}
                </Typography>
              </Box>
            ))}
          </Box>
        </>
      ) : null}

      <PartnerTripRequestDrawer
        open={drawerOpen}
        requestId={selectedRequestId}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedRequestId(null);
        }}
        onChanged={() => {
          loadData();
          onChanged?.();
        }}
      />

      <CreateOpsRequestDialog
        open={createOpen}
        mode="partner"
        tripId={tripId}
        tripLabel={trip?.title || trip?.name || ''}
        onClose={() => setCreateOpen(false)}
        onCreated={(created) => {
          loadData();
          onChanged?.();
          if (created?.id) {
            setSelectedRequestId(created.id);
            setDrawerOpen(true);
          }
        }}
      />
    </Box>
  );
};

export default PartnerTripOperationsPanel;
